import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useTranslation } from 'react-i18next'
import { 
  CheckCircle,
  Info,
  Plus,
  Eye,
  EyeOff,
  ArrowRight
} from 'lucide-react'
import { useCategoriesStore } from '@flows.cash/core'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { useDefaultCategories } from '@flows.cash/core'

interface CategorySetupStepProps {
  onNext: () => void
}

export function CategorySetupStep({ onNext }: CategorySetupStepProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { saveCategories, defaultCategories: storeDefaultCategories } = useCategoriesStore()
  const defaultCategories = useDefaultCategories()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showIncomeCategories, setShowIncomeCategories] = useState(true)
  const [showExpenseCategories, setShowExpenseCategories] = useState(true)

  const incomeCategories = defaultCategories.filter((cat: any) => cat.type === 'income')
  const expenseCategories = defaultCategories.filter((cat: any) => cat.type === 'expense')

  const handleContinue = async () => {
    if (!user?.id) {
      toast.error('You must be logged in to set up categories')
      return
    }

    setIsSubmitting(true)
    
    try {
      // Initialize default categories for the user
      await saveCategories(user.id, storeDefaultCategories)
      toast.success('Categories set up successfully!')
      onNext()
    } catch (error) {
      console.error('Error setting up categories:', error)
      toast.error('Failed to set up categories. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCustomize = () => {
    // TODO: Open a category management modal or navigate to categories page
    toast.info('Category customization will be available after onboarding!')
  }

  const CategoryGroup = ({ 
    title, 
    categories, 
    type, 
    isVisible, 
    onToggleVisibility 
  }: { 
    title: string
    categories: any[]
    type: 'income' | 'expense'
    isVisible: boolean
    onToggleVisibility: () => void
  }) => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-base flex items-center gap-2">
          {title}
          <Badge variant="secondary" className="text-xs">
            {categories.length} categories
          </Badge>
        </h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleVisibility}
          className="text-xs h-8"
        >
          {isVisible ? (
            <>
              <EyeOff className="h-3 w-3 mr-1" />
              Hide
            </>
          ) : (
            <>
              <Eye className="h-3 w-3 mr-1" />
              Show
            </>
          )}
        </Button>
      </div>

      {isVisible && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {categories.map((category: any) => (
            <Card
              key={category.id}
              className="p-3 border transition-all duration-200 hover:shadow-md bg-gradient-to-br from-white/50 to-white/30 dark:from-gray-800/50 dark:to-gray-800/30"
            >
              <div className="flex items-start gap-3">
                <div 
                  className="p-2 rounded-lg shadow-sm"
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  <div 
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: category.color }}
                  />
                </div>
                <div className="space-y-1 flex-1 min-w-0">
                  <h5 className="font-medium text-sm">{category.name}</h5>
                  <p className="text-xs text-muted-foreground">
                    {category.subcategories?.length || 0} subcategories
                  </p>
                </div>
                <CheckCircle className="h-4 w-4 text-[#439476] flex-shrink-0" />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="relative mx-auto w-16 h-16 mb-4">
          <div className="absolute inset-0 bg-gradient-to-r from-[#439476] to-[#387565] rounded-xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-[#439476] to-[#387565] rounded-xl p-3 shadow-lg">
            <CheckCircle className="h-10 w-10 text-white" />
          </div>
        </div>
        
        <h3 className="text-xl font-bold bg-gradient-to-r from-[#1C1C1C] to-[#383838] dark:from-[#E8E8E8] dark:to-[#CCCCCC] bg-clip-text text-transparent">
          {t('onboarding.categorySetup.title', 'Your Categories Are Ready')}
        </h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {t('onboarding.categorySetup.description', 'We\'ve prepared a comprehensive set of categories to help you track your finances. Review them below and customize later if needed.')}
        </p>
      </div>

      {/* Info Card */}
      <Card className="p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 text-sm">
              {t('onboarding.categorySetup.tipTitle', 'Complete Category System')}
            </h4>
            <p className="text-blue-700 dark:text-blue-300 text-sm">
              {t('onboarding.categorySetup.tipDescription', 'Your account includes income and expense categories with detailed subcategories. You can customize these anytime in the Categories section.')}
            </p>
          </div>
        </div>
      </Card>

      {/* Categories Overview */}
      <div className="space-y-6">
        {/* Income Categories */}
        <CategoryGroup
          title="💰 Income Categories"
          categories={incomeCategories}
          type="income"
          isVisible={showIncomeCategories}
          onToggleVisibility={() => setShowIncomeCategories(!showIncomeCategories)}
        />

        {/* Expense Categories */}
        <CategoryGroup
          title="💳 Expense Categories"
          categories={expenseCategories}
          type="expense"
          isVisible={showExpenseCategories}
          onToggleVisibility={() => setShowExpenseCategories(!showExpenseCategories)}
        />
      </div>

      {/* Summary Card */}
      <Card className="p-4 bg-[#439476]/5 border-[#439476]/20">
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="font-medium">{incomeCategories.length}</span>
              <span className="text-muted-foreground">Income</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="font-medium">{expenseCategories.length}</span>
              <span className="text-muted-foreground">Expense</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="font-medium">
                {defaultCategories.reduce((total: number, cat: any) => total + (cat.subcategories?.length || 0), 0)}
              </span>
              <span className="text-muted-foreground">Subcategories</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('onboarding.categorySetup.summaryHelp', 'All categories will be added to your account and ready to use immediately.')}
          </p>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <Button
          variant="outline"
          onClick={handleCustomize}
          className="flex-1 sm:flex-none"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('onboarding.categorySetup.customize', 'Customize Later')}
        </Button>
        
        <Button
          onClick={handleContinue}
          disabled={isSubmitting}
          className="flex-1 bg-gradient-to-r from-[#439476] to-[#387565] hover:from-[#387565] hover:to-[#2F5E54] text-white shadow-lg"
        >
          {isSubmitting ? (
            'Setting up...'
          ) : (
            <>
              {t('onboarding.categorySetup.continue', 'Continue with These Categories')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  )
} 